import type { KeyboardEvent, MouseEvent } from 'react';
import clsx from 'clsx';
import { StatusPill } from './StatusPill';
import { IconButton } from './IconButton';
import { LivePreview } from './LivePreview';
import { CardActionsMenu } from './CardActionsMenu';
import type { Camera } from '@api/types';

interface Props {
  camera: Camera;
  onToggleFavorite: (camera: Camera) => void;
  onToggleStatus: (camera: Camera) => void;
  onOpenSettings: (camera: Camera) => void;
  onOpenPlayback: (camera: Camera) => void;
  onDelete: (camera: Camera) => void;
  onContextMenu: (camera: Camera, event: MouseEvent<HTMLElement>) => void;
  canManageCameras: boolean;
}

export function CameraCard({
  camera,
  onToggleFavorite,
  onToggleStatus,
  onOpenSettings,
  onOpenPlayback,
  onDelete,
  onContextMenu,
  canManageCameras,
}: Props): JSX.Element {
  const isOnline = camera.status === 'active';
  const isRecording = isOnline && camera.recordingMode === 'continuous' && camera.recordingEnabled;
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onOpenPlayback(camera);
  };

  return (
    <article
      role="button"
      tabIndex={0}
      className={clsx(
        'group flex h-full min-h-0 flex-col overflow-hidden rounded-card border border-border bg-surface',
        'cursor-pointer shadow-card transition-colors hover:border-accent/50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
      )}
      onClick={() => onOpenPlayback(camera)}
      onKeyDown={handleKeyDown}
      onContextMenu={(e) => {
        if (!canManageCameras) return;
        e.preventDefault();
        onContextMenu(camera, e);
      }}
    >
      {/* Preview fluidly fills the remaining card height after the meta strip. */}
      <div className="relative min-h-0 w-full flex-1 bg-bg">
        <LivePreview cameraId={camera.id} status={camera.status} />

        <div className="absolute left-2 top-2 z-10">
          <StatusPill status={camera.status} />
        </div>

        <div className="absolute right-2 top-2 z-10 flex gap-1">
          {canManageCameras ? (
            <>
              <FavoriteButton
                camera={camera}
                isRecording={isRecording}
                onToggleFavorite={onToggleFavorite}
              />
              <CameraActions
                camera={camera}
                onOpenSettings={onOpenSettings}
                onDelete={onDelete}
              />
            </>
          ) : null}
        </div>
      </div>

      <div className="flex shrink-0 flex-col gap-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="truncate text-sm font-semibold text-foreground" title={camera.name}>
            {camera.name}
          </h3>
          {canManageCameras ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onToggleStatus(camera);
              }}
              className={clsx(
                'shrink-0 rounded border border-border px-2 py-0.5 text-[11px] uppercase tracking-wide text-muted',
                'transition-colors hover:border-accent hover:text-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              )}
            >
              {isOnline ? 'Disable' : 'Enable'}
            </button>
          ) : null}
        </div>

        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <Meta label="Group" value={camera.group?.name ?? '—'} />
          <Meta label="Type" value={camera.type} />
          <Meta label="Mode" value={camera.recordingMode} />
          {/* <Meta label="Storage" value={camera.storageTarget} /> */}
        </dl>
      </div>
    </article>
  );
}

interface FavoriteButtonProps {
  camera: Camera;
  isRecording: boolean;
  onToggleFavorite: (camera: Camera) => void;
}

function FavoriteButton({
  camera,
  isRecording,
  onToggleFavorite,
}: FavoriteButtonProps): JSX.Element {
  return (
    <IconButton
      label={
        isRecording
          ? `${camera.isFavorite ? 'Remove from favorites' : 'Add to favorites'} (Recording active)`
          : camera.isFavorite
            ? 'Remove from favorites'
            : 'Add to favorites'
      }
      active={camera.isFavorite}
      onClick={(e) => {
        e.stopPropagation();
        onToggleFavorite(camera);
      }}
    >
      <span className="relative inline-flex items-center justify-center">
        <span className={camera.isFavorite ? 'text-warning' : ''}>★</span>
        {isRecording ? (
          <span
            className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-danger shadow-sm shadow-danger/60"
            aria-hidden
          />
        ) : null}
      </span>
    </IconButton>
  );
}

interface CameraActionsProps {
  camera: Camera;
  onOpenSettings: (camera: Camera) => void;
  onDelete: (camera: Camera) => void;
}

function CameraActions({
  camera,
  onOpenSettings,
  onDelete,
}: CameraActionsProps): JSX.Element {
  return (
    <CardActionsMenu
      actions={[
        {
          id: 'settings',
          label: 'Settings',
          onSelect: () => onOpenSettings(camera),
        },
        {
          id: 'delete',
          label: 'Delete camera',
          variant: 'danger',
          onSelect: () => onDelete(camera),
        },
      ]}
    />
  );
}

interface MetaProps {
  label: string;
  value: string;
}

function Meta({ label, value }: MetaProps): JSX.Element {
  return (
    <div className="flex items-center gap-1 truncate">
      <dt className="text-muted">{label}</dt>
      <dd className="truncate font-medium text-foreground capitalize">{value}</dd>
    </div>
  );
}
